import {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import Table from 'rsuite/Table'
import Tooltip from 'rsuite/Tooltip'
import Whisper from 'rsuite/Whisper'
import Pagination from 'rsuite/Pagination'
import IconButton from 'rsuite/esm/IconButton'
import {FiEye} from 'react-icons/fi'
import {isEmpty} from 'lodash'
import {StateParamsModel} from 'src/app/modules/common/Model'
import CheckPermissions from 'src/app/modules/common/checkPermission'
import DesignComponent from 'src/app/modules/common/components/designComponent'
import RSuiteTable from 'src/cms/helpers/components/rsuiteTable'
import * as fixedDepositRequestsRedux from '../redux'
import {service} from '../redux/service'
import ViewFixedDepositRequestDetails from './ViewDetails'

const STATUS_OPTIONS = [
  {label: 'All', value: ''},
  {label: 'Valid', value: 'valid'},
  {label: 'Invalid', value: 'invalid'},
]

const FixedDepositRequestsReport = () => {
  const dispatch = useDispatch()
  const {
    data: {data: requests, total},
    loading,
  } = useSelector((state: any) => state.fixedDepositRequestsReport)

  const [params, setParams] = useState<StateParamsModel>({page: 1, limit: 15})
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [showDetails, setShowDetails] = useState(false)

  useEffect(() => {
    dispatch(fixedDepositRequestsRedux.actions.getFixedDepositRequests(params))
  }, [params])

  const Cell = Table.Cell

  const ActionCell = ({rowData, ...props}: any) => (
    <Cell {...props} className='link-group'>
      <CheckPermissions type='Edit'>
        <Whisper placement='top' trigger='hover' speaker={<Tooltip>View Details</Tooltip>}>
          <IconButton
            appearance='subtle'
            icon={<FiEye />}
            onClick={() => {
              setSelectedId(rowData.id)
              setShowDetails(true)
            }}
          />
        </Whisper>
      </CheckPermissions>
    </Cell>
  )

  const columns = [
    {label: 'S.N.', dataKey: 'sn', width: 60, cell: <Cell dataKey='sn' />},
    {
      label: 'Full Name',
      dataKey: 'full_name',
      flexGrow: 1,
      cell: <Cell>{(rowData: any) => rowData.full_name || '-'}</Cell>,
    },
    {
      label: 'Account Number',
      dataKey: 'account_number',
      flexGrow: 1,
      cell: <Cell>{(rowData: any) => rowData.account_number || '-'}</Cell>,
    },
    {
      label: 'Mobile',
      dataKey: 'mobile_number',
      width: 130,
      cell: <Cell>{(rowData: any) => rowData.mobile_number || '-'}</Cell>,
    },
    {
      label: 'FD Type',
      dataKey: 'fd_type',
      width: 150,
      cell: <Cell>{(rowData: any) => rowData.fd_type || '-'}</Cell>,
    },
    {
      label: 'Tenure',
      dataKey: 'tenure',
      width: 110,
      cell: <Cell>{(rowData: any) => rowData.tenure || '-'}</Cell>,
    },
    {
      label: 'Currency',
      dataKey: 'currency',
      width: 100,
      cell: <Cell>{(rowData: any) => rowData.currency || '-'}</Cell>,
    },
    {
      label: 'Deposit Amount',
      dataKey: 'deposit_amount',
      width: 150,
      cell: <Cell>{(rowData: any) => rowData.deposit_amount || '-'}</Cell>,
    },
    {
      label: 'Status',
      dataKey: 'account_status',
      width: 120,
      cell: <Cell>{(rowData: any) => rowData.account_status || '-'}</Cell>,
    },
    {label: 'Action', width: 80, align: 'center', cell: <ActionCell dataKey='id' />},
  ]

  const tableData = !isEmpty(requests)
    ? requests.map((item: any, i: number) => ({
        sn: (params.page - 1) * params.limit + (i + 1),
        id: item.id,
        full_name: item.full_name,
        account_number: item.account_number,
        mobile_number: item.mobile_number,
        fd_type: item.fd_type?.title,
        tenure: item.fd_tenure ? `${item.fd_tenure.tenure} ${item.fd_tenure.tenure_by}` : null,
        currency: item.fd_currency?.title,
        deposit_amount: item.deposit_amount,
        account_status: item.account_status,
      }))
    : []

  const handleRefresh = () => {
    dispatch(fixedDepositRequestsRedux.actions.getFixedDepositRequests(params))
  }

  const handleExport = () => {
    service.exportFixedDepositRequests(params).then((res) => {
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'fixed-deposit-requests.xlsx')
      document.body.appendChild(link)
      link.click()
      link.remove()
    })
  }

  return (
    <>
      <div className='shadow p-3 bg-white rounded'>
        <DesignComponent
          moduleName='Fixed Deposit Requests'
          params={params}
          setParams={setParams}
          filterData={true}
          handleRefresh={handleRefresh}
          addShow={false}
          deleteShow={false}
          importShow={false}
          toggleMultipleShow={false}
          exportShow={true}
          handleExport={handleExport}
          enableDropdownFilter={true}
          dropdownOption={STATUS_OPTIONS}
          handleDropdownChange={(option: any) => {
            setParams({...params, status: option?.value, page: 1})
          }}
          dropdownValue={
            STATUS_OPTIONS.find((o) => o.value === String(params.status ?? '')) ||
            STATUS_OPTIONS[0]
          }
          dropdownPlaceholder='Filter by Status'
          loading={loading}
        />

        <div className='datatable'>
          <RSuiteTable
            onChecked={() => {}}
            showCheckbox={false}
            columns={columns}
            showLoading={loading}
            data={tableData}
            checkedValues={[]}
          />
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            className='mt-5'
            maxButtons={5}
            size='sm'
            layout={['total', '-', 'limit', '|', 'pager', 'skip']}
            total={total || 0}
            limitOptions={[10, 20, 50]}
            limit={params.limit}
            activePage={params.page}
            onChangePage={(value) => setParams({...params, page: value})}
            onChangeLimit={(value) => setParams({...params, limit: value})}
          />
        </div>
      </div>

      {showDetails && selectedId && (
        <ViewFixedDepositRequestDetails id={selectedId} handleClose={() => setShowDetails(false)} />
      )}
    </>
  )
}

export default FixedDepositRequestsReport
